/** Mask and clipping intents. */
import type { MaskClipIntent, MaskInvertIntent, MaskRemoveIntent, MaskUpdateIntent } from '@maipo/agent-protocol';
import { InvertMaskCommand, RemoveMaskCommand, UpdateLayerCommand, UpdateMaskCommand } from '../../core/commands';
import { findLayer } from '../../core/model';
import { defineIntent, type Diagnostic } from '../registry';
import { checkUnlocked, error, layerFor, requireLayer, resolveRef, staticIds } from './common';

function footprint(ref: string, aliases: ReadonlyMap<string, string>) {
  const ids = staticIds([ref], aliases);
  return { reads: ids, writes: ids, structural: false, document: false };
}

export const maskClip = defineIntent<MaskClipIntent>({
  type: 'mask.clip',
  version: 1,
  footprint: (_document, intent, aliases) => footprint(intent.layer, aliases),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (layer) checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => {
    const layer = requireLayer(document, intent.layer, aliases);
    return [new UpdateLayerCommand(layer.id, { clipping: intent.enabled }, intent.enabled ? 'Clip to layer below' : 'Release clipping')];
  },
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases) ?? intent.layer;
    return [{ layer: id, path: 'clipping', before: findLayer(document.root, id)?.clipping ?? null, after: intent.enabled }];
  },
});

export const maskUpdate = defineIntent<MaskUpdateIntent>({
  type: 'mask.update',
  version: 1,
  footprint: (_document, intent, aliases) => footprint(intent.layer, aliases),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (!layer.mask) diagnostics.push(error('COMMAND_REJECTED', `Layer ${layer.id} has no mask.`, layer.id));
    if (intent.linked === false) diagnostics.push(error('COMMAND_REJECTED', 'Independent mask transforms are not supported yet.', layer.id));
    checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => {
    const layer = requireLayer(document, intent.layer, aliases);
    return [new UpdateMaskCommand(layer.id, { ...(intent.enabled === undefined ? {} : { enabled: intent.enabled }), ...(intent.linked === undefined ? {} : { linked: intent.linked }) })];
  },
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases) ?? intent.layer;
    const mask = findLayer(document.root, id)?.mask;
    return [
      ...(intent.enabled === undefined ? [] : [{ layer: id, path: 'mask.enabled', before: mask?.enabled ?? null, after: intent.enabled }]),
      ...(intent.linked === undefined ? [] : [{ layer: id, path: 'mask.linked', before: mask?.linked ?? null, after: intent.linked }]),
    ];
  },
});

export const maskInvert = defineIntent<MaskInvertIntent>({
  type: 'mask.invert',
  version: 1,
  footprint: (_document, intent, aliases) => footprint(intent.layer, aliases),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (!layer.mask) diagnostics.push(error('COMMAND_REJECTED', `Layer ${layer.id} has no mask.`, layer.id));
    checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => [new InvertMaskCommand(requireLayer(document, intent.layer, aliases).id)],
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases) ?? intent.layer;
    const mask = findLayer(document.root, id)?.mask;
    return [{ layer: id, path: 'mask.defaultAlpha', before: mask?.defaultAlpha ?? null, after: mask ? 255 - mask.defaultAlpha : null }];
  },
});

export const maskRemove = defineIntent<MaskRemoveIntent>({
  type: 'mask.remove',
  version: 1,
  footprint: (_document, intent, aliases) => footprint(intent.layer, aliases),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (!layer.mask) diagnostics.push({ severity: 'info', code: 'no-mask', message: `Layer ${layer.id} has no mask; nothing to remove.`, layer: layer.id });
    checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => [new RemoveMaskCommand(requireLayer(document, intent.layer, aliases).id)],
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases) ?? intent.layer;
    const mask = findLayer(document.root, id)?.mask;
    return mask ? [{ layer: id, path: 'mask', before: { id: mask.id, enabled: mask.enabled }, after: null }] : [];
  },
});
